import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/axios";
import { Deal } from "@/types/lead";

interface ChartFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: string;
  projectSelected?: string;
  userSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
}

interface DealsByAdvisorData {
  advisor: string;
  advisorId: string;
  statuses: {
    statusName: string;
    statusId: string;
    count: number;
    color: string;
  }[];
  total: number;
}

interface FunnelHealthData {
  statusName: string;
  statusId: string;
  order: number;
  reached: number;
  won: number;
  conversionRate: number;
}

interface WeeklyData {
  dealsAppointmentEfectivity: string;
  dealsArrived: number;
  dealsArrivedEfectivity: string;
  dealsWithAppointment: number;
  dealsWithConfirmedAppointment: number;
  dealsWon: number;
  dealsWonEfectivity: string;
  label: string;
  leadsCount: number;
  leadsEfectivity: string;
  weekEnd: string;
  weekStart: string;
}

export const useDealsByAdvisor = (filters: ChartFilters) => {
  return useQuery({
    queryKey: ["deals-by-advisor", filters],
    queryFn: async ({ queryKey }) => {
      const [, filters] = queryKey as [string, ChartFilters];

      const response: { data: DealsByAdvisorData[] } = await api.get(
        "/dashboard/deals-by-advisor",
        {
          params: {
            startDate: filters.startDate?.toISOString(),
            endDate: filters.endDate?.toISOString(),
            userId: filters.userId,
            projectSelected: filters.projectSelected,
            userSelected: filters.userSelected,
            type: filters.type,
            region: filters.region,
            cordination: filters.cordination,
            principal: filters.principal,
            master: filters.master,
          },
        }
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useFunnelHealth = (filters: ChartFilters) => {
  return useQuery({
    queryKey: ["funnel-health", filters],
    queryFn: async ({ queryKey }): Promise<FunnelHealthData[]> => {
      const [, filters] = queryKey as [string, ChartFilters];
      const response = await api.get(`/dashboard/funnel-health`, {
        params: {
          startDate: filters.startDate?.toISOString(),
          endDate: filters.endDate?.toISOString(),
          userId: filters.userId,
          projectSelected: filters.projectSelected,
          userSelected: filters.userSelected,
          type: filters.type,
          region: filters.region,
          cordination: filters.cordination,
          principal: filters.principal,
          master: filters.master,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useWeekly = (filters: ChartFilters) => {
  return useQuery({
    queryKey: ["detail", filters],
    queryFn: async ({ queryKey }): Promise<WeeklyData[]> => {
      const [, filters] = queryKey as [string, ChartFilters];
      const response = await api.get(`/dashboard/detail`, {
        params: {
          startDate: filters.startDate?.toISOString(),
          endDate: filters.endDate?.toISOString(),
          userId: filters.userId,
          campaign: filters.projectSelected,
          username: filters.userSelected,
          type: filters.type,
          region: filters.region,
          cordination: filters.cordination,
          principal: filters.principal,
          master: filters.master,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAppointmentTable = (filters: ChartFilters) => {
  return useQuery({
    queryKey: ["appointments", filters],
    queryFn: async ({ queryKey }): Promise<Deal[]> => {
      const [, filters] = queryKey as [string, ChartFilters];
      const response = await api.get(`/dashboard/appointment`, {
        params: {
          startDate: filters.startDate?.toISOString(),
          endDate: filters.endDate?.toISOString(),
          userId: filters.userId,
          campaign: filters.projectSelected,
          userSelected: filters.userSelected,
          type: filters.type,
          region: filters.region,
          cordination: filters.cordination,
          principal: filters.principal,
          master: filters.master,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
